// FUNDS-REQUESTS-INTAKE-1 / FR-C — Operations → Funds Intake → Requests mode.
// Cross-claim funds-request work queue: who was asked for money, how much they owe,
// how old it is, what's outstanding, follow-up. Visibility-scoped + TPA-walled server-side
// (GET /reimbursements). Client-side search/filter/sort/aging over the returned set.
import { useState, useEffect, useMemo } from "react";
import { apiFetch, fmt, can } from "../api";
import { StatusPill as UiPill } from "./ui";  // UI-SWEEP-ACCOUNTING
import { StatusPill, downloadReimbExport } from "./Reimbursements";
import EntityDocuments from "./EntityDocuments";  // DC-4b: funds-request support docs

const KIND = { underwriter: "Underwriters", assured: "Assured", broker: "Broker", tpa: "TPA", other: "Other" };
const kindLabel = (k) => (k ? String(k).split(", ").map((x) => KIND[x] || x).join(", ") : "—");
const AGING = [["all", "All"], ["current", "Current"], ["1-30", "1–30"], ["31-60", "31–60"], ["61-90", "61–90"], ["90+", "90+"]];
const bucketOf = (d) => (d == null || d <= 0 ? "current" : d <= 30 ? "1-30" : d <= 60 ? "31-60" : d <= 90 ? "61-90" : "90+");
const QBO_ST = { sent: ["#dbeafe", "#1e40af", "Sent"], partial: ["#fef3c7", "#92400e", "Partial"], paid: ["#dcfce7", "#166534", "Paid"], voided: ["#f1f5f9", "#64748b", "Void"] };
function qboStatusBadge(s) {
  if (!s) return <span style={{ fontSize: 10, color: "var(--text3)" }}>—</span>;
  const tone = { sent: "blue", partial: "amber", paid: "green", voided: "slate" }[s] || "slate";
  const label = (QBO_ST[s] || [null, null, s])[2];
  return <UiPill sm tone={tone}>{label}</UiPill>;
}

export function FundsIntakeRequests({ onNavigateToClaim }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("outstanding");
  const [kind, setKind] = useState("all");
  const [aging, setAging] = useState("all");
  const [sort, setSort] = useState({ key: "age_days", dir: "desc" });
  const [fuId, setFuId] = useState(null);
  const [fuForm, setFuForm] = useState({ next_follow_up_at: "", follow_up_status: "", follow_up_note: "" });
  const [busy, setBusy] = useState(false);
  const [qboMsg, setQboMsg] = useState(null);
  const [details, setDetails] = useState({});
  const loadDetail = (id) => { apiFetch(`/reimbursements/${id}`).then(d => setDetails(p => ({ ...p, [id]: d }))).catch(() => {}); };

  const load = () => apiFetch(`/reimbursements${status ? `?status=${encodeURIComponent(status)}` : ""}`)
    .then((d) => { setData(d); setErr(null); })
    .catch((e) => setErr(String((e && e.message) || e)));
  useEffect(() => { load(); /* refetch when the server-side status filter changes */ }, [status]);

  const rows = useMemo(() => {
    let r = (data && data.requests) || [];
    if (q) { const s = q.toLowerCase(); r = r.filter((x) => (x.claim_number || "").toLowerCase().includes(s) || (x.insured_name || "").toLowerCase().includes(s) || (x.lead_adjuster_name || "").toLowerCase().includes(s) || (x.recipient_names || "").toLowerCase().includes(s)); }
    if (kind !== "all") r = r.filter((x) => (x.recipient_kinds || "").includes(kind));
    if (aging !== "all") r = r.filter((x) => bucketOf(x.age_days) === aging);
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...r].sort((a, b) => { const av = a[sort.key], bv = b[sort.key]; if (av == null) return 1; if (bv == null) return -1; return (av > bv ? 1 : av < bv ? -1 : 0) * dir; });
  }, [data, q, kind, aging, sort]);

  const recordFollowUp = async (id) => {
    setBusy(true);
    try {
      await apiFetch(`/reimbursements/${id}/follow-up`, { method: "PATCH", body: {
        next_follow_up_at: fuForm.next_follow_up_at || null,
        follow_up_status: fuForm.follow_up_status || null,
        follow_up_note: fuForm.follow_up_note || null } });
      setFuId(null); setFuForm({ next_follow_up_at: "", follow_up_status: "", follow_up_note: "" }); load();
    } catch (e) { setErr(String((e && e.message) || e)); } finally { setBusy(false); }
  };

  const sendToQbo = async (id) => {
    if (!window.confirm("Create a QBO Trust invoice for each payor on this request? This pushes to QuickBooks.")) return;
    setBusy(true); setQboMsg(null);
    try {
      const r = await apiFetch(`/reimbursements/${id}/send-to-qbo`, { method: "POST", body: {} });
      setQboMsg({ id, text: `Sent to QBO — ${r.created} invoice(s) created${r.skipped ? `, ${r.skipped} skipped` : ""}.` });
      load();
    } catch (e) { setQboMsg({ id, text: "Error: " + String((e && e.message) || e) }); } finally { setBusy(false); }
  };
  const refreshQbo = async (id) => {
    setBusy(true); setQboMsg(null);
    try {
      const r = await apiFetch(`/reimbursements/${id}/qbo-refresh`, { method: "POST", body: {} });
      setQboMsg({ id, text: `Refreshed — ${r.recipients_updated} invoice(s) read, ${r.receipts_posted} new receipt(s)${r.detail ? `. Status: ${r.detail.status}` : ""}.` });
      load();
    } catch (e) { setQboMsg({ id, text: "Error: " + String((e && e.message) || e) }); } finally { setBusy(false); }
  };

  const th = (key, label, align) => (
    <th onClick={key ? () => setSort((p) => ({ key, dir: p.key === key && p.dir === "asc" ? "desc" : "asc" })) : undefined}
      style={{ padding: "6px 10px", fontSize: 11, color: "white", background: "var(--navy)", textAlign: align || "left",
               whiteSpace: "nowrap", cursor: key ? "pointer" : "default", userSelect: "none" }}>
      {label}{sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
  const td = { padding: "6px 10px", borderBottom: "1px solid var(--border)", fontSize: 12 };
  const num = { ...td, textAlign: "right", fontFamily: "ui-monospace,monospace", whiteSpace: "nowrap" };
  const sel = { fontSize: 12, padding: "5px 8px" };
  const sm = (data && data.summary) || {};

  return (
    <div>
      <div style={{ display: "flex", gap: 18, marginBottom: 12, flexWrap: "wrap" }}>
        <Metric label="Outstanding" value={fmt.money(sm.total_outstanding || 0)} strong />
        <Metric label="Requested" value={fmt.money(sm.total_requested || 0)} />
        <Metric label="Received" value={fmt.money(sm.total_received || 0)} />
        <Metric label="Open requests" value={sm.open_count != null ? String(sm.open_count) : "—"} />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...sel, minWidth: 220 }} placeholder="Search claim # / insured / adjuster / payor" value={q} onChange={(e) => setQ(e.target.value)} />
        <select style={sel} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="outstanding">Outstanding (Sent + Partial)</option>
          <option value="">All statuses</option>
          <option value="Draft">Draft</option><option value="Sent">Sent</option>
          <option value="Partially Received">Partially Received</option>
          <option value="Received">Received</option><option value="Closed">Closed</option><option value="Void">Void</option>
        </select>
        <select style={sel} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="all">All recipients</option>
          {Object.entries(KIND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span style={{ display: "inline-flex", gap: 4 }}>
          {AGING.map(([k, lbl]) => (
            <button key={k} onClick={() => setAging(k)}
              style={{ fontSize: 11, padding: "4px 9px", borderRadius: 12, border: "1px solid var(--border)", cursor: "pointer",
                       background: aging === k ? "var(--navy)" : "transparent", color: aging === k ? "white" : "var(--text2)" }}>{lbl}</button>
          ))}
        </span>
        <button className="btn btn-outline btn-sm" style={{ marginLeft: "auto" }}
          onClick={() => downloadReimbExport(`/reports/reimbursement-requests/export${status && status !== "" ? `?status=${encodeURIComponent(status)}` : ""}`).catch((e) => setErr(String(e.message || e)))}>⤓ Export XLSX</button>
      </div>

      {err && <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 8 }}>{err}</div>}

      <div className="card" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            {th("request_date", "Request Date")}{th("claim_number", "Claim")}{th("recipient_kinds", "Recipient")}
            {th("request_type", "Type")}{th("amount_requested", "Requested", "right")}{th("received_total", "Received", "right")}
            {th("outstanding_total", "Outstanding", "right")}{th("age_days", "Days Out", "right")}
            {th("status", "Status")}{th(null, "QBO")}{th("lead_adjuster_name", "Lead Adjuster")}{th(null, "Follow-up")}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={12} style={{ ...td, color: "var(--text3)", textAlign: "center", padding: "16px" }}>No funds requests match.</td></tr>}
            {rows.map((r) => (
              <FragmentRow key={r.id} r={r} td={td} num={num} onNavigateToClaim={onNavigateToClaim}
                open={fuId === r.id} onToggle={() => { const opening = fuId !== r.id; setFuId(opening ? r.id : null); if (opening) loadDetail(r.id); setFuForm({ next_follow_up_at: r.next_follow_up_at || "", follow_up_status: r.follow_up_status || "", follow_up_note: r.follow_up_note || "" }); }}
                fuForm={fuForm} setFuForm={setFuForm} onSave={() => recordFollowUp(r.id)} busy={busy} kindLabel={kindLabel}
                onSendQbo={() => sendToQbo(r.id)} onRefreshQbo={() => refreshQbo(r.id)} qboMsg={qboMsg && qboMsg.id === r.id ? qboMsg.text : null} detail={details[r.id]} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ label, value, strong }) {
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", color: "var(--text3)", letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: strong ? 700 : 600, fontFamily: "ui-monospace,monospace", color: strong ? "var(--navy)" : "var(--text)" }}>{value}</div>
    </div>
  );
}

function FragmentRow({ r, td, num, onNavigateToClaim, open, onToggle, fuForm, setFuForm, onSave, busy, kindLabel, onSendQbo, onRefreshQbo, qboMsg, detail }) {
  const inp = { fontSize: 12, padding: "5px 8px", width: "100%", boxSizing: "border-box" };
  return (
    <>
      <tr onClick={onToggle} style={{ background: open ? "#f8fafc" : "transparent", cursor: "pointer" }}>
        <td style={{ ...td, whiteSpace: "nowrap", color: "var(--text2)" }}>{fmt.date(r.request_date)}</td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>{onNavigateToClaim ? <span onClick={(e) => { e.stopPropagation(); onNavigateToClaim(r.claim_id); }} style={{ color: "var(--blue,#2E75B6)", cursor: "pointer" }}>{r.claim_number}</span> : r.claim_number}<div style={{ fontSize: 10, color: "var(--text3)" }}>{r.insured_name || ""}</div></td>
        <td style={td}>{kindLabel(r.recipient_kinds)}</td>
        <td style={{ ...td, color: "var(--text2)" }}>{r.request_type}</td>
        <td style={num}>{fmt.money(r.amount_requested)}</td>
        <td style={num}>{fmt.money(r.received_total)}</td>
        <td style={{ ...num, color: (r.outstanding_total || 0) > 0 && r.status !== "Void" ? "var(--navy)" : "var(--text3)", fontWeight: 600 }}>{fmt.money(r.outstanding_total)}</td>
        <td style={{ ...num, color: (r.age_days || 0) >= 60 ? "#b45309" : "var(--text2)" }}>{r.age_days != null ? r.age_days : "—"}</td>
        <td style={td}><StatusPill status={r.status} /></td>
        <td style={td}>{r.qbo_synced
          ? <span className="badge" style={{ fontSize: 9, background: "#dcfce7", color: "#166534" }} title={r.qbo_fully_synced ? "All payors synced to QBO" : "Partially synced to QBO"}>QBO {r.qbo_fully_synced ? "✓" : "•"}</span>
          : (r.status !== "Void" && r.status !== "Closed" && <button className="btn btn-outline btn-sm" style={{ padding: "1px 7px", fontSize: 10 }} disabled={busy} onClick={(e) => { e.stopPropagation(); onSendQbo(); }}>Sync QBO</button>)}</td>
        <td style={{ ...td, color: "var(--text2)", whiteSpace: "nowrap" }}>{r.lead_adjuster_name || "—"}</td>
        <td style={td}><button className="btn btn-outline btn-sm" style={{ padding: "2px 8px", fontSize: 11 }} onClick={(e) => { e.stopPropagation(); onToggle(); }}>{r.next_follow_up_at ? `⚑ ${fmt.date(r.next_follow_up_at)}` : "Follow-up"}</button></td>
      </tr>
      {open && (
        <tr><td colSpan={12} style={{ padding: "10px 12px", background: "#f8fafc", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div><div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 3 }}>Next follow-up</div>
              <input type="date" style={inp} value={fuForm.next_follow_up_at || ""} onChange={(e) => setFuForm({ ...fuForm, next_follow_up_at: e.target.value })} /></div>
            <div><div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 3 }}>Status</div>
              <input style={{ ...inp, minWidth: 150 }} placeholder="e.g. Emailed underwriter" value={fuForm.follow_up_status || ""} onChange={(e) => setFuForm({ ...fuForm, follow_up_status: e.target.value })} /></div>
            <div style={{ flex: 1, minWidth: 200 }}><div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 3 }}>Note</div>
              <input style={inp} placeholder="Follow-up note" value={fuForm.follow_up_note || ""} onChange={(e) => setFuForm({ ...fuForm, follow_up_note: e.target.value })} /></div>
            <button className="btn btn-primary btn-sm" onClick={onSave} disabled={busy}>{busy ? "Saving…" : "Record follow-up"}</button>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {qboMsg && <span style={{ fontSize: 11, color: "var(--text2)", marginRight: "auto" }}>{qboMsg}</span>}
            <span style={{ fontSize: 11, color: "var(--text2)", fontWeight: 600 }}>QuickBooks Trust:</span>
            {r.status !== "Void" && r.status !== "Closed" &&
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={onSendQbo}>Send to QBO</button>}
            <button className="btn btn-outline btn-sm" disabled={busy} onClick={onRefreshQbo}>↻ Refresh from QBO</button>
          </div>
          {detail && detail.recipients && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text2)", marginBottom: 4 }}>Payor participants</div>
              <table style={{ width: "100%", fontSize: 12 }}>
                <thead><tr>{["Payor", "Slip %", "Allocated", "Received", "QBO"].map((h, i) => <th key={i} style={{ textAlign: i > 0 ? "right" : "left", padding: "2px 8px", fontSize: 10, color: "var(--text3)", textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                <tbody>
                  {detail.recipients.map(rc => (
                    <tr key={rc.id} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={{ padding: "3px 8px" }}>{rc.display_name || "—"}{rc.is_lead && <span style={{ marginLeft: 5, fontSize: 9, color: "#991b1b" }}>LEAD</span>}</td>
                      <td style={{ padding: "3px 8px", textAlign: "right", fontFamily: "ui-monospace,monospace" }}>{rc.participation_pct != null ? Number(rc.participation_pct).toFixed(2) + "%" : "—"}</td>
                      <td style={{ padding: "3px 8px", textAlign: "right", fontFamily: "ui-monospace,monospace" }}>{fmt.money(rc.allocated_amount)}</td>
                      <td style={{ padding: "3px 8px", textAlign: "right", fontFamily: "ui-monospace,monospace", color: (rc.received_amount || 0) > 0 ? "#166534" : "var(--text3)" }}>{fmt.money(rc.received_amount)}</td>
                      <td style={{ padding: "3px 8px", textAlign: "right" }}>{qboStatusBadge(rc.qbo_invoice_status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {r.last_follow_up_at && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>Last follow-up: {fmt.datetime(r.last_follow_up_at)}{r.follow_up_note ? ` — ${r.follow_up_note}` : ""}</div>}
          {/* DC-4b (2026-06-17): funds-request support docs (request statement, receipts backup). */}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)" }}>
            <EntityDocuments entityType="funds_request" entityId={r.id} canEdit={can("manage_reimbursement_intake")} defaultPhi={false} title="Funds request documents" />
          </div>
        </td></tr>
      )}
    </>
  );
}
